import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ConfirmDialog, Label, Textarea } from '@bliks-io/ui';
import { useRejectEntitySuggestion } from '@/api/mutations/useRejectEntitySuggestion';
import { toastApiError } from '@/api/mutations/toast-api-error';

interface RejectConfirmDialogProps {
  open: boolean;
  suggestionId: string;
  onCancel: () => void;
  /** Called after the mutation resolves successfully so the parent can close the review modal. */
  onSuccess: () => void;
}

/**
 * Destructive confirmation dialog for rejecting an entity suggestion. Hosts
 * the required rejection-reason textarea inline so the admin confirms intent
 * and provides the reason in a single step.
 */
export function RejectConfirmDialog({
  open,
  suggestionId,
  onCancel,
  onSuccess
}: RejectConfirmDialogProps) {
  const { t } = useTranslation();
  const reject = useRejectEntitySuggestion(suggestionId);
  const [reason, setReason] = useState('');

  const isSubmitting = reject.isPending;
  const isValid = reason.trim().length > 0;

  const handleConfirm = async () => {
    if (!isValid) return;
    try {
      await reject.mutateAsync({ rejectionReason: reason.trim() });
      toast.success(t('entitySuggestion.review.rejected'));
      setReason('');
      onSuccess();
    } catch (err) {
      console.error('Failed to reject suggestion:', err);
      toastApiError(err, 'common.errorOccurred');
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setReason('');
    onCancel();
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
      title={t('entitySuggestion.review.confirmReject.title')}
      description={t('entitySuggestion.review.confirmReject.description')}
      confirmLabel={t('entitySuggestion.review.reject')}
      variant="danger"
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      isPending={isSubmitting}
      isConfirmDisabled={!isValid}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reject-reason">{t('entitySuggestion.review.fields.rejectionReason')}</Label>
        <Textarea
          id="reject-reason"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('entitySuggestion.review.placeholders.rejectionReason')}
          disabled={isSubmitting}
        />
      </div>
    </ConfirmDialog>
  );
}
